import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AutoTranslate from "@/components/site/AutoTranslate";
import ExitIntent from "@/components/site/ExitIntent";
import SiteGuide from "@/components/site/SiteGuide";
import TrackingScripts from "@/components/site/TrackingScripts";
import { trackPageview, trackPageviewLeave } from "@/lib/api";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Pricing from "@/pages/Pricing";
import FreeAudit from "@/pages/FreeAudit";
import StartProject from "@/pages/StartProject";
import ProjectSuccess from "@/pages/ProjectSuccess";
import Playbook from "@/pages/Playbook";
import WebDesignLanding from "@/pages/WebDesignLanding";
import Clients from "@/pages/Clients";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Tools from "@/pages/Tools";
import ToolDetail from "@/pages/ToolDetail";
import FloorPlannerPage from "@/pages/tools/floorplanner/FloorPlannerPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Recommended from "@/pages/Recommended";
import Contact from "@/pages/Contact";
import Aveno from "@/pages/Aveno";
import Kai from "@/pages/Kai";
import ProductDetail from "@/pages/ProductDetail";
import BuildPreview from "@/pages/BuildPreview";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

let lenisInstance = null;

// Visitors arriving on the shop subdomain (e.g. shop.juliusmason.com) land
// directly on the AVENO shop instead of the studio homepage.
function isShopHost() {
  return typeof window !== "undefined" && window.location.hostname.toLowerCase().startsWith("shop.");
}

function ScrollManager() {
  const { pathname } = useLocation();
  const viewIdRef = useRef(null);
  const enteredAtRef = useRef(0);

  useEffect(() => {
    if (lenisInstance) lenisInstance.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);

    // Close out the previous page's view (fires when the user navigates
    // internally). The unload listener below handles tab-close.
    if (viewIdRef.current && enteredAtRef.current) {
      trackPageviewLeave(viewIdRef.current, Date.now() - enteredAtRef.current);
    }
    enteredAtRef.current = Date.now();
    trackPageview(pathname).then((id) => { viewIdRef.current = id; });
  }, [pathname]);

  useEffect(() => {
    const flush = () => {
      if (viewIdRef.current && enteredAtRef.current) {
        trackPageviewLeave(viewIdRef.current, Date.now() - enteredAtRef.current);
      }
    };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
    };
  }, []);

  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <AutoTranslate />
      <TrackingScripts />
      <Navbar />
      <main>{children}</main>
      <Footer />
      <SiteGuide />
      <ExitIntent />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisInstance = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollManager />
        <AuthProvider>
          <LanguageProvider>
            <CurrencyProvider>
            <Routes>
              <Route path="/" element={isShopHost() ? <Navigate to="/shop" replace /> : <PublicLayout><Home /></PublicLayout>} />
              <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
              <Route path="/free-website-audit" element={<PublicLayout><FreeAudit /></PublicLayout>} />
              <Route path="/start-project" element={<PublicLayout><StartProject /></PublicLayout>} />
              <Route path="/build" element={<PublicLayout><BuildPreview /></PublicLayout>} />
              <Route path="/start-project/success" element={<PublicLayout><ProjectSuccess /></PublicLayout>} />
              <Route path="/web-design" element={<PublicLayout><WebDesignLanding /></PublicLayout>} />
              <Route path="/web-design/:slug" element={<PublicLayout><WebDesignLanding /></PublicLayout>} />
              <Route path="/web-design/:slug/:sub" element={<PublicLayout><WebDesignLanding /></PublicLayout>} />
              <Route path="/free-playbook" element={<PublicLayout><Playbook /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
              <Route path="/services/:slug" element={<PublicLayout><ServiceDetail /></PublicLayout>} />
              <Route path="/clients" element={<PublicLayout><Clients /></PublicLayout>} />
              <Route path="/projects" element={<PublicLayout><Projects /></PublicLayout>} />
              <Route path="/projects/:id" element={<PublicLayout><ProjectDetail /></PublicLayout>} />
              <Route path="/tools" element={<PublicLayout><Tools /></PublicLayout>} />
              <Route path="/tools/floor-planner" element={<FloorPlannerPage />} />
              <Route path="/tools/:slug" element={<PublicLayout><ToolDetail /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
              <Route path="/shop" element={<PublicLayout><Recommended /></PublicLayout>} />
              <Route path="/shop/product/:handle" element={<PublicLayout><ProductDetail /></PublicLayout>} />
              <Route path="/aveno" element={<PublicLayout><Aveno /></PublicLayout>} />
              <Route path="/kai" element={<PublicLayout><Kai /></PublicLayout>} />
              <Route path="/recommended" element={<Navigate to="/shop" replace />} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
            </Routes>
            </CurrencyProvider>
            </LanguageProvider>
        </AuthProvider>
        <Toaster position="bottom-center" theme="dark" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
